// activeRecalcModel — data side of the "recalculation running" pill (task 458.4, ADR-073 D12).
// Kept apart from the component so the badge file exports components only (react-refresh rule).

import { useQuery } from '@tanstack/react-query';
import { getActiveRecalcJobs, type ActiveRecalcJobDto } from '../../../api/endpoints/gl';
import { queryKeys } from '../../../api/queryKeys';

/** Re-read the list every 10 s while a job is active and every 30 s otherwise, so a nightly or back-dated job shows up without a reload. */
export const ACTIVE_JOBS_REFETCH_ACTIVE_MS = 10_000;
export const ACTIVE_JOBS_REFETCH_IDLE_MS = 30_000;

const JOB_TYPE_LABEL: Record<string, string> = {
  ENTRIES_BASE_REBUILD: 'Entries base rebuild',
  REVALUATION_RANGE: 'Revaluation',
  BALANCE_RECALC: 'Balance recalculation',
  ACCOUNT_BASE_RECALC: 'Account recalculation',
};

export function useActiveRecalcJobs(): ActiveRecalcJobDto[] {
  const { data } = useQuery({
    queryKey: queryKeys.gl.activeJobs(),
    queryFn: getActiveRecalcJobs,
    refetchInterval: (query) =>
      query.state.data?.length ? ACTIVE_JOBS_REFETCH_ACTIVE_MS : ACTIVE_JOBS_REFETCH_IDLE_MS,
    staleTime: 5_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return data ?? [];
}

/** One tooltip line per job: type, mode, range and the progress the runner has reported. */
export function describeActiveJob(job: ActiveRecalcJobDto): string {
  const type = JOB_TYPE_LABEL[job.jobType] ?? job.jobType;
  const mode = job.mode ? ` (${job.mode})` : '';
  const range = job.fromDate === job.toDate ? job.fromDate : `${job.fromDate} → ${job.toDate}`;
  const progress =
    job.totalDays > 0
      ? `${job.processedDays}/${job.totalDays} days`
      : job.totalAccounts > 0
        ? `${job.processedAccounts}/${job.totalAccounts} accounts`
        : job.status;
  return `${type}${mode} ${range} — ${job.status}, ${progress}`;
}

export function activeJobsLabel(count: number): string {
  return count === 1 ? 'Recalculation running' : `${count} recalculations running`;
}
